import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  VStack,
  HStack,
  Text,
  IconButton,
  Tooltip,
  Collapse,
  useDisclosure,
  Switch,
  Badge,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Alert,
  AlertIcon,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiSettings, FiDownload, FiSave, FiTrash2, FiPlus, FiMinus } from 'react-icons/fi';

const CsvExportConfig = ({ 
  multiTableData, 
  onExportCsv, 
  projectId,
  resultId 
}) => {
  const toast = useToast();
  const { isOpen: isConfigOpen, onToggle: onConfigToggle } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  
  // 設定状態
  const [exportConfigs, setExportConfigs] = useState([]); // 保存済み設定
  const [currentConfig, setCurrentConfig] = useState(null); // 現在の設定
  const [selectedConfigId, setSelectedConfigId] = useState(''); // 選択された設定ID
  const [saveConfigName, setSaveConfigName] = useState(''); // 保存用設定名
  
  // 出力設定状態
  const [tableSelections, setTableSelections] = useState({}); // テーブルごとの選択状態
  const [outputMode, setOutputMode] = useState('flat'); // 出力モード: 'flat' または 'separate'
  const [outputFormat, setOutputFormat] = useState('csv'); // 出力形式: 'csv' または 'json'
  
  // 初期化：multiTableDataが変更されたときに選択状態を初期化
  useEffect(() => {
    if (multiTableData && multiTableData.length > 0) {
      const initialSelections = {};
      multiTableData.forEach((table, tableIndex) => {
        if (table.data && table.data.length > 0) {
          const allColumns = Object.keys(table.data[0]);
          // id と _editable は表示しない
          const columns = allColumns.filter(col => col !== 'id' && col !== '_editable');
          initialSelections[tableIndex] = {
            enabled: true,
            tableName: table.title || `テーブル${tableIndex + 1}`,
            customTableName: table.title || `テーブル${tableIndex + 1}`,
            repeatToAllRows: false, // すべての行に値を反復するかどうか
            columns: columns.reduce((acc, col) => {
              acc[col] = {
                enabled: true,
                originalName: col,
                customName: col
              };
              return acc;
            }, {})
          };
        }
      });
      setTableSelections(initialSelections);
    }
  }, [multiTableData]);

  // 保存済み設定を読み込み
  useEffect(() => {
    loadExportConfigs();
  }, [projectId]);

  // 設定の読み込み
  const loadExportConfigs = () => {
    try {
      const saved = localStorage.getItem(`csv_export_configs_${projectId}`);
      if (saved) {
        setExportConfigs(JSON.parse(saved));
      }
    } catch (error) {
      console.error('設定読み込みエラー:', error);
    }
  };

  // 設定の保存
  const saveExportConfig = () => {
    if (!saveConfigName.trim()) {
      toast({
        title: 'エラー',
        description: '設定名を入力してください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newConfig = {
      id: Date.now().toString(),
      name: saveConfigName,
      tableSelections: { ...tableSelections },
      outputMode: outputMode,
      outputFormat: outputFormat,
      createdAt: new Date().toISOString(),
    };

    const updatedConfigs = [...exportConfigs, newConfig];
    setExportConfigs(updatedConfigs);
    
    try {
      localStorage.setItem(`csv_export_configs_${projectId}`, JSON.stringify(updatedConfigs));
      toast({
        title: '設定を保存しました',
        description: `「${saveConfigName}」として保存されました`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setSaveConfigName('');
      onSaveModalClose();
    } catch (error) {
      console.error('設定保存エラー:', error);
      toast({
        title: 'エラー',
        description: '設定の保存に失敗しました',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 設定の読み込み
  const loadExportConfig = (configId) => {
    const config = exportConfigs.find(c => c.id === configId);
    if (config) {
      setTableSelections(config.tableSelections);
      setOutputMode(config.outputMode || 'flat'); // デフォルトはflat
      setOutputFormat(config.outputFormat || 'csv'); // デフォルトはcsv
      setCurrentConfig(config);
      toast({
        title: '設定を読み込みました',
        description: `「${config.name}」を適用しました`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // 設定の削除
  const deleteExportConfig = (configId) => {
    const updatedConfigs = exportConfigs.filter(c => c.id !== configId);
    setExportConfigs(updatedConfigs);
    localStorage.setItem(`csv_export_configs_${projectId}`, JSON.stringify(updatedConfigs));
    
    if (selectedConfigId === configId) {
      setSelectedConfigId('');
      setCurrentConfig(null);
    }
    
    toast({
      title: '設定を削除しました',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // テーブルの有効/無効切り替え
  const toggleTableEnabled = (tableIndex) => {
    setTableSelections(prev => ({
      ...prev,
      [tableIndex]: {
        ...prev[tableIndex],
        enabled: !prev[tableIndex]?.enabled
      }
    }));
  };

  // テーブル名の変更
  const updateTableName = (tableIndex, newName) => {
    setTableSelections(prev => ({
      ...prev,
      [tableIndex]: {
        ...prev[tableIndex],
        customTableName: newName
      }
    }));
  };

  // テーブルの値反復設定を切り替え
  const toggleTableRepeat = (tableIndex) => {
    setTableSelections(prev => ({
      ...prev,
      [tableIndex]: {
        ...prev[tableIndex],
        repeatToAllRows: !prev[tableIndex]?.repeatToAllRows
      }
    }));
  };

  // カラムの有効/無効切り替え
  const toggleColumnEnabled = (tableIndex, columnName) => {
    setTableSelections(prev => ({
      ...prev,
      [tableIndex]: {
        ...prev[tableIndex],
        columns: {
          ...prev[tableIndex]?.columns,
          [columnName]: {
            ...prev[tableIndex]?.columns?.[columnName],
            enabled: !prev[tableIndex]?.columns?.[columnName]?.enabled
          }
        }
      }
    }));
  };

  // カラム名の変更
  const updateColumnName = (tableIndex, columnName, newName) => {
    setTableSelections(prev => ({
      ...prev,
      [tableIndex]: {
        ...prev[tableIndex],
        columns: {
          ...prev[tableIndex]?.columns,
          [columnName]: {
            ...prev[tableIndex]?.columns?.[columnName],
            customName: newName
          }
        }
      }
    }));
  };

  // CSV出力の実行
  const handleExportCsv = () => {
    const enabledTables = Object.keys(tableSelections)
      .filter(tableIndex => tableSelections[tableIndex]?.enabled)
      .map(tableIndex => {
        const tableConfig = tableSelections[tableIndex];
        const tableData = multiTableData[parseInt(tableIndex)];
        
        if (!tableData?.data) return null;

        const enabledColumns = Object.keys(tableConfig.columns)
          .filter(col => tableConfig.columns[col]?.enabled)
          .map(col => ({
            original: col,
            custom: tableConfig.columns[col]?.customName || col
          }));

        return {
          tableName: tableConfig.customTableName,
          columns: enabledColumns,
          data: tableData.data
        };
      })
      .filter(Boolean);

    if (enabledTables.length === 0) {
      toast({
        title: 'エラー',
        description: '出力するテーブルが選択されていません',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // データ出力の実行
    onExportCsv(enabledTables, outputMode, outputFormat, tableSelections);
  };

  if (!multiTableData || multiTableData.length === 0) {
    return null;
  }

  return (
    <Box mt={6} p={4} bg="gray.50" borderRadius="md">
      {/* ヘッダー部分 */}
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between">
          <VStack align="start" spacing={1}>
            <HStack>
              <Text fontWeight="bold" fontSize="lg">出力設定</Text>
              <Badge colorScheme="blue">{multiTableData.length} テーブル</Badge>
            </HStack>
            <Text fontSize="xs" color="gray.600">
              出力形式・ファイル形式を選択し、各テーブルの出力内容をカスタマイズできます
            </Text>
          </VStack>
        </HStack>
        
        {/* 出力オプション選択 */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <HStack>
            <Text fontSize="sm" fontWeight="bold">出力形式:</Text>
            <Select
              value={outputMode}
              onChange={(e) => setOutputMode(e.target.value)}
              size="sm"
              width="140px"
            >
              <option value="flat">フラット形式</option>
              <option value="separate">テーブル分離</option>
            </Select>
          </HStack>
          
          <HStack>
            <Text fontSize="sm" fontWeight="bold">ファイル形式:</Text>
            <Select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              size="sm"
              width="100px"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
            </Select>
          </HStack>
        </SimpleGrid>
        
        {/* コントロールパネル */}
        <HStack justify="space-between">
          {/* 保存済み設定の選択 */}
          <Select
            placeholder="保存済み設定を選択"
            value={selectedConfigId}
            onChange={(e) => {
              setSelectedConfigId(e.target.value);
              if (e.target.value) {
                loadExportConfig(e.target.value);
              }
            }}
            size="sm"
            width="200px"
          >
            {exportConfigs.map(config => (
              <option key={config.id} value={config.id}>
                {config.name}
              </option>
            ))}
          </Select>
          
          <Tooltip label="設定を表示/非表示">
            <IconButton
              icon={<FiSettings />}
              size="sm"
              onClick={onConfigToggle}
              aria-label="設定表示切り替え"
            />
          </Tooltip>
          
          <Tooltip label="設定を保存">
            <IconButton
              icon={<FiSave />}
              size="sm"
              colorScheme="blue"
              onClick={onSaveModalOpen}
              aria-label="設定保存"
            />
          </Tooltip>
          
          <Tooltip label={`${outputFormat.toUpperCase()}ファイルをダウンロード`}>
            <Button
              leftIcon={<FiDownload />}
              colorScheme="green"
              size="sm"
              onClick={handleExportCsv}
            >
              {outputFormat.toUpperCase()}出力
            </Button>
          </Tooltip>
        </HStack>
      </VStack>

      {/* 設定詳細パネル */}
      <Collapse in={isConfigOpen}>
        <VStack spacing={4} align="stretch">
          {multiTableData.map((table, tableIndex) => {
            const tableConfig = tableSelections[tableIndex];
            if (!tableConfig || !table.data) return null;

            const allColumns = Object.keys(table.data[0] || {});
            // id と _editable は表示しない
            const columns = allColumns.filter(col => col !== 'id' && col !== '_editable');
            
            return (
              <Box key={tableIndex} p={4} bg="white" borderRadius="md" border="1px" borderColor="gray.200">
                <VStack spacing={3} align="stretch">
                  {/* テーブルヘッダー */}
                  <VStack spacing={3} align="stretch">
                    <HStack justify="space-between">
                      <HStack>
                        <Switch
                          isChecked={tableConfig.enabled}
                          onChange={() => toggleTableEnabled(tableIndex)}
                          colorScheme="blue"
                        />
                        <FormControl width="200px">
                          <Input
                            value={tableConfig.customTableName}
                            onChange={(e) => updateTableName(tableIndex, e.target.value)}
                            size="sm"
                            placeholder="テーブル名"
                          />
                        </FormControl>
                      </HStack>
                      <Badge colorScheme={tableConfig.enabled ? 'green' : 'gray'}>
                        {columns.filter(col => tableConfig.columns[col]?.enabled).length} / {columns.length} カラム
                      </Badge>
                    </HStack>
                    
                    {/* 値反復設定 */}
                    <VStack align="start" spacing={1}>
                      <HStack>
                        <Checkbox
                          isChecked={tableConfig.repeatToAllRows}
                          onChange={() => toggleTableRepeat(tableIndex)}
                          colorScheme="orange"
                          size="sm"
                        >
                          <Text fontSize="sm">このテーブルの値をすべての行に反復</Text>
                        </Checkbox>
                        {tableConfig.repeatToAllRows && (
                          <Badge colorScheme="orange" size="sm">反復ON</Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.500" ml={6}>
                        ヘッダー情報（会社名等）を全明細行に含めたい場合にチェック
                      </Text>
                    </VStack>
                  </VStack>

                  {/* カラム設定 */}
                  {tableConfig.enabled && (
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" mb={1}>カラム設定:</Text>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        チェックを入れたカラムが出力され、右側で指定した名前がファイルのカラム名になります
                      </Text>
                      <VStack spacing={2} align="stretch">
                        {columns.map(column => {
                          const columnConfig = tableConfig.columns[column];
                          if (!columnConfig) return null;

                          return (
                            <HStack key={column} spacing={3}>
                              <Checkbox
                                isChecked={columnConfig.enabled}
                                onChange={() => toggleColumnEnabled(tableIndex, column)}
                                colorScheme="blue"
                              />
                              <Text fontSize="sm" width="120px" color="gray.600">
                                {column}
                              </Text>
                              <Text fontSize="sm" width="20px" textAlign="center">→</Text>
                              <Input
                                value={columnConfig.customName}
                                onChange={(e) => updateColumnName(tableIndex, column, e.target.value)}
                                size="sm"
                                placeholder="出力カラム名"
                                width="200px"
                                isDisabled={!columnConfig.enabled}
                              />
                            </HStack>
                          );
                        })}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      </Collapse>

      {/* 設定保存モーダル */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>CSV出力設定を保存</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>設定名</FormLabel>
                <Input
                  value={saveConfigName}
                  onChange={(e) => setSaveConfigName(e.target.value)}
                  placeholder="例: 請求書用設定"
                />
              </FormControl>

              {exportConfigs.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>保存済み設定:</Text>
                  <VStack spacing={2} align="stretch">
                    {exportConfigs.map(config => (
                      <HStack key={config.id} justify="space-between" p={2} bg="gray.50" borderRadius="md">
                        <Text fontSize="sm">{config.name}</Text>
                        <IconButton
                          icon={<FiTrash2 />}
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => deleteExportConfig(config.id)}
                          aria-label="設定削除"
                        />
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSaveModalClose}>
              キャンセル
            </Button>
            <Button colorScheme="blue" onClick={saveExportConfig}>
              保存
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CsvExportConfig;